import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  InputAdornment,
  Chip,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { coachingProgramAPI, userAPI, amenityAPI, amenityScheduleAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { parseDateOnlyString, formatDateForInput } from '../../utils/dateUtils';
import ReservationResultsDialog from './ReservationResultsDialog';
import { CoachingProgram } from '../../types/coaching';
import { useAuth } from '../../context/AuthContext';
import { validateCoachingTimeSlotAlignment, formatSlotAlignmentMessage, dateToTimeString } from '../../utils/slotValidation';
import { UserRole } from '@/types';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface ProgramFormData {
  name: string;
  type: string;
  description: string;
  coachId: number | null;
  isGuestCoach: boolean;
  guestCoachName: string;
  venues: string[]; // Array of amenity IDs
  level: string;
  capacity: number;
  startDate: Date;
  endDate: Date;
  recurringPattern: {
    frequency: 'daily' | 'weekly' | 'monthly' | '';
    interval: number;
    daysOfWeek: number[];
    dayOfMonth?: number;
  };
  sessionTime: Date;
  sessionDurationMinutes: number;
  totalCost: string;
  dropInCost: string;
  enrollmentDeadline: Date | null;
  allowDropIns: boolean;
  notes: string;
  image: string;
}

const EditCoachingProgramPage: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [program, setProgram] = useState<CoachingProgram | null>(null);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [conflictingEnrollments, setConflictingEnrollments] = useState<any[]>([]);
  
  // Reservation result dialog state
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);
  
  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Accordion states
  const [recurringConfigExpanded, setRecurringConfigExpanded] = useState(false);
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(false);

  // Slot validation state
  const [slotValidationWarning, setSlotValidationWarning] = useState<string | null>(null);
  const [venueSchedules, setVenueSchedules] = useState<Map<string, any[]>>(new Map());
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [loadingVenueSchedules, setLoadingVenueSchedules] = useState<boolean>(false);

  // Effect to scroll to top when error occurs
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const [formData, setFormData] = useState<ProgramFormData>({
    name: '',
    type: '',
    description: '',
    coachId: null,
    isGuestCoach: false,
    guestCoachName: '',
    venues: [],
    level: '',
    capacity: 1,
    startDate: new Date(),
    endDate: new Date(),
    recurringPattern: {
      frequency: 'weekly',
      interval: 1,
      daysOfWeek: [new Date().getDay()],
    },
    sessionTime: new Date(),
    sessionDurationMinutes: 60,
    totalCost: '',
    dropInCost: '',
    enrollmentDeadline: null,
    allowDropIns: false,
    notes: '',
    image: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch coaches
        const coachesResponse = await userAPI.getCoaches();
        setCoaches(coachesResponse.data);

        // Fetch amenities
        const amenitiesResponse = await amenityAPI.getAmenities();
        setAmenities(amenitiesResponse.data);
        
        // Build venue names map
        const names = new Map<string, string>();
        amenitiesResponse.data.forEach((amenity: any) => {
          names.set(amenity.id.toString(), amenity.name);
        });
        setVenueNames(names);

        // Fetch program details if editing
        if (programId) {
          const programResponse = await coachingProgramAPI.getCoachingProgramById(programId);
          const programData = programResponse.data;
          setProgram(programData);
          
          // Parse time string for editing
          const parseTimeForEdit = (timeString: string | undefined | null): Date => {
            if (!timeString) {
              console.warn('parseTimeForEdit: No time string provided');
              return new Date();
            }
            try {
              // Check if it's an ISO timestamp (contains 'T')
              if (timeString.includes('T')) {
                // It's a full ISO timestamp, parse it directly
                const date = new Date(timeString);
                if (isNaN(date.getTime())) {
                  console.error('Invalid ISO timestamp:', timeString);
                  return new Date();
                }
                return date;
              }
              
              // It's a time-only string (HH:MM:SS format)
              const timeOnly = timeString.split('+')[0].split('-')[0];
              const [hours, minutes, seconds] = timeOnly.split(':').map(Number);
              const date = new Date();
              date.setHours(hours, minutes, seconds || 0, 0);
              return date;
            } catch (error) {
              console.error('Error parsing time string:', timeString, error);
              return new Date();
            }
          };
          
          // Determine the session time - check multiple sources
          const sessionTime = programData.recurringPattern?.startTime || 
                              programData.sessionTime || 
                              null;
          
          console.log('Program data sessionTime sources:', {
            recurringPatternStartTime: programData.recurringPattern?.startTime,
            sessionTime: programData.sessionTime,
            selectedSessionTime: sessionTime
          });
          
          setFormData({
            name: programData.name,
            type: programData.type || '',
            description: programData.description || '',
            coachId: programData.coachId,
            isGuestCoach: programData.isGuestCoach || false,
            guestCoachName: programData.guestCoachName || '',
            venues: programData.venues || [],
            level: programData.level,
            capacity: programData.capacity,
            startDate: parseDateOnlyString(programData.startDate),
            endDate: parseDateOnlyString(programData.endDate),
            recurringPattern: {
              frequency: programData.recurringPattern?.frequency || 'weekly',
              interval: programData.recurringPattern?.interval || 1,
              daysOfWeek: (() => {
                // Ensure start date's day of week is always included
                const startDateDayOfWeek = parseDateOnlyString(programData.startDate).getDay();
                const existingDays = programData.recurringPattern?.daysOfWeek || [];
                
                if (programData.recurringPattern?.frequency === 'weekly' && !existingDays.includes(startDateDayOfWeek)) {
                  return [...existingDays, startDateDayOfWeek].sort();
                }
                return existingDays.length > 0 ? existingDays : [startDateDayOfWeek];
              })(),
              dayOfMonth: programData.recurringPattern?.dayOfMonth
            },
            sessionTime: sessionTime ? parseTimeForEdit(sessionTime) : new Date(),
            sessionDurationMinutes: programData.recurringPattern?.duration || 
              programData.sessionDurationMinutes || 60,
            totalCost: programData.totalCost?.toString() || '',
            dropInCost: programData.dropInCost?.toString() || '',
            enrollmentDeadline: programData.enrollmentDeadline ? parseDateOnlyString(programData.enrollmentDeadline) : null,
            allowDropIns: programData.allowDropIns,
            notes: programData.notes || '',
            image: programData.image || ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load program data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [programId]);

  // Fetch schedules for selected venues
  const fetchVenueSchedules = async (venueIds: string[]) => {
    if (venueIds.length === 0) {
      setVenueSchedules(new Map());
      return;
    }

    try {
      setLoadingVenueSchedules(true);
      const schedules = new Map<string, any[]>();

      // Fetch schedules for each venue
      await Promise.all(
        venueIds.map(async (venueId) => {
          try {
            const response = await amenityScheduleAPI.getAmenitySchedules(venueId);
            schedules.set(venueId, response.data);
          } catch (error) {
            console.error(`Error fetching schedule for venue ${venueId}:`, error);
            schedules.set(venueId, []);
          }
        })
      );

      setVenueSchedules(schedules);
    } catch (error) {
      console.error('Error fetching venue schedules:', error);
    } finally {
      setLoadingVenueSchedules(false);
    }
  };

  // Validate slot alignment when venues or times change
  const validateSlotAlignment = async () => {
    if (formData.venues.length === 0) {
      setSlotValidationWarning(null);
      return;
    }

    const startTimeStr = dateToTimeString(formData.sessionTime);
    const endTimeStr = (() => {
      const endDate = new Date(formData.sessionTime);
      endDate.setMinutes(endDate.getMinutes() + formData.sessionDurationMinutes);
      return dateToTimeString(endDate);
    })();

    // Use startDate as representative date for validation
    const result = await validateCoachingTimeSlotAlignment(
      formData.venues,
      venueSchedules,
      venueNames,
      formData.startDate,
      startTimeStr,
      endTimeStr
    );

    if (!result.valid) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else if (result.warnings.length > 0) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else {
      setSlotValidationWarning(null);
    }
  };

  // Effect to fetch venue schedules when venues change
  useEffect(() => {
    if (formData.venues.length > 0) {
      fetchVenueSchedules(formData.venues);
    } else {
      setVenueSchedules(new Map());
      setSlotValidationWarning(null);
    }
  }, [formData.venues]);

  // Effect to validate slot alignment when schedules are loaded or times change
  useEffect(() => {
    if (venueSchedules.size > 0 && formData.venues.length > 0) {
      validateSlotAlignment();
    }
  }, [venueSchedules, formData.sessionTime, formData.sessionDurationMinutes, formData.startDate]);

  // Helper function to safely handle date changes from DatePicker
  const handleDateChange = (field: 'startDate' | 'endDate' | 'enrollmentDeadline') => (date: Date | null) => {
    console.log(`DatePicker onChange for ${field}:`, date);
    
    if (!date) {
      if (field === 'enrollmentDeadline') {
        setFormData(prev => ({ ...prev, [field]: null }));
      } else {
        setFormData(prev => ({ ...prev, [field]: new Date() }));
      }
      return;
    }
    
    // Create a new date object that represents the same calendar date
    // regardless of timezone by using the local date components
    const safeDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    console.log(`Safe date for ${field}:`, safeDate);
    
    // If changing the start date and we have a weekly recurring pattern, 
    // auto-include the new start date's day of week
    if (field === 'startDate' && formData.recurringPattern.frequency === 'weekly') {
      const newDayOfWeek = safeDate.getDay();
      const currentDays = formData.recurringPattern.daysOfWeek || [];
      
      // Add the new day if it's not already included
      if (!currentDays.includes(newDayOfWeek)) {
        const updatedDays = [...currentDays, newDayOfWeek].sort();
        setFormData(prev => ({ 
          ...prev, 
          [field]: safeDate,
          recurringPattern: {
            ...prev.recurringPattern,
            daysOfWeek: updatedDays
          }
        }));
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: safeDate }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccessMessage(null);
    
    // Collect missing required fields
    const missingFields = [];
    
    if (!formData.name.trim()) {
      missingFields.push('Program Name');
    }
    
    if (!formData.type.trim()) {
      missingFields.push('Program Type');
    }
    
    if (!formData.isGuestCoach && !formData.coachId) {
      missingFields.push('Coach');
    }
    
    if (formData.isGuestCoach && !formData.guestCoachName.trim()) {
      missingFields.push('Guest Coach Name');
    }
    
    if (!formData.venues || formData.venues.length === 0) {
      missingFields.push('Venues');
    }
    
    if (!formData.level.trim()) {
      missingFields.push('Level');
    }
    
    if (formData.capacity < 1) {
      missingFields.push('Valid Capacity');
    }
    
    if (!formData.startDate) {
      missingFields.push('Start Date');
    }
    
    if (!formData.endDate) {
      missingFields.push('End Date');
    }
    
    if (!formData.recurringPattern.frequency) {
      missingFields.push('Recurring Pattern Frequency');
    }
    
    if (formData.totalCost === '' || formData.totalCost === null || formData.totalCost === undefined) {
      missingFields.push('Total Cost');
    } else if (parseFloat(formData.totalCost) < 0) {
      missingFields.push('Valid Total Cost (cannot be negative)');
    }
    
    // Check if dropInCost is valid when drop-ins are allowed
    if (formData.allowDropIns) {
      if (formData.dropInCost === '' || formData.dropInCost === null || formData.dropInCost === undefined) {
        missingFields.push('Drop-in Cost (when drop-ins are enabled)');
      } else if (parseFloat(formData.dropInCost) < 0) {
        missingFields.push('Valid Drop-in Cost (cannot be negative)');
      }
    }
    
    // Show validation error if fields are missing
    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Date validation
    if (formData.endDate <= formData.startDate) {
      setError('End date must be after start date');
      return;
    }
    
    // Enrollment deadline validation (if set)
    if (formData.enrollmentDeadline && formData.enrollmentDeadline >= formData.startDate) {
      setError('Enrollment deadline must be before the program start date');
      return;
    }
    
    // Validate recurring pattern
    if (formData.recurringPattern.frequency === 'weekly' && formData.recurringPattern.daysOfWeek.length === 0) {
      setError('Please select at least one day of the week for weekly recurring programs');
      return;
    }
    
    if (formData.recurringPattern.frequency === 'monthly' && !formData.recurringPattern.dayOfMonth) {
      setError('Please specify the day of the month for monthly recurring programs');
      return;
    }

    try {
      setSaving(true);
      
      // Convert sessionTime Date object to HH:MM string format (like coaching sessions do)
      const sessionTimeString = typeof formData.sessionTime === 'string' 
        ? formData.sessionTime 
        : formData.sessionTime.toTimeString().split(' ')[0].slice(0, 5);
      
      // Convert dates to YYYY-MM-DD string format using formatDateForInput
      const startDateString = formatDateForInput(formData.startDate);
      const endDateString = formatDateForInput(formData.endDate);
      const enrollmentDeadlineString = formData.enrollmentDeadline ? formatDateForInput(formData.enrollmentDeadline) : undefined;
      
      // Calculate end time from start time and duration
      const calculateEndTime = (startTime: string, durationMinutes: number): string => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        date.setMinutes(date.getMinutes() + durationMinutes);
        return date.toTimeString().split(' ')[0].slice(0, 5);
      };
      
      const endTimeString = calculateEndTime(sessionTimeString, formData.sessionDurationMinutes);
      
      const programData: any = {
        name: formData.name.trim(),
        type: formData.type.trim(),
        description: formData.description.trim(),
        coachId: formData.isGuestCoach ? undefined : formData.coachId!,
        isGuestCoach: formData.isGuestCoach,
        guestCoachName: formData.isGuestCoach ? formData.guestCoachName.trim() : undefined,
        venues: formData.venues,
        level: formData.level.trim(),
        capacity: formData.capacity,
        recurringPattern: {
          frequency: formData.recurringPattern.frequency,
          interval: formData.recurringPattern.interval,
          daysOfWeek: formData.recurringPattern.daysOfWeek,
          dayOfMonth: formData.recurringPattern.dayOfMonth,
          startTime: sessionTimeString,
          endTime: endTimeString,
          duration: formData.sessionDurationMinutes
        },
        sessionTime: sessionTimeString,
        sessionDurationMinutes: formData.sessionDurationMinutes,
        totalCost: parseFloat(formData.totalCost),
        dropInCost: formData.allowDropIns && formData.dropInCost !== '' && formData.dropInCost !== null && formData.dropInCost !== undefined
          ? parseFloat(formData.dropInCost)
          : 0,
        allowDropIns: formData.allowDropIns,
        enrollmentDeadline: formData.enrollmentDeadline,
        notes: formData.notes.trim(),
        image: formData.image.trim()
      };
      
      // Only include startDate if it has changed from the original
      if (program && startDateString !== program.startDate) {
        programData.startDate = startDateString;
      }
      
      // Only include endDate if it has changed from the original
      if (program && endDateString !== program.endDate) {
        programData.endDate = endDateString;
      }

      if (programId) {
        const response = await coachingProgramAPI.updateCoachingProgram(programId, programData);
        
        // Show reservation results dialog if there's a result
        if (response.data.reservationResult) {
          setReservationDialogData({
            entityType: 'program',
            entityName: formData.name,
            operationType: 'update',
            reservationResult: response.data.reservationResult
          });
          setReservationDialogOpen(true);
          
          // Navigate back after dialog is shown
          // setTimeout(() => {
          //   navigate(-1);
          // }, 2500);
        } else {
          setSuccessMessage('Program updated successfully!');
          // Navigate back after short delay
          // setTimeout(() => {
          //   navigate(-1);
          // }, 1500);
        }
      }
      
    } catch (error: any) {
      console.error('Error saving coaching program:', error);
      
      // Check if error contains conflicting enrollments information
      if (error.response?.data?.activeEnrollments) {
        setConflictingEnrollments(error.response.data.activeEnrollments);
      } else {
        setConflictingEnrollments([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save coaching program';
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProgram = () => {
    setError(null);
    setConflictingEnrollments([]);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteProgram = async () => {
    if (!programId) return;

    try {
      setDeleting(true);
      setError(null);
      
      await coachingProgramAPI.deleteCoachingProgram(programId);
      
      setSuccessMessage('Program deleted successfully!');
      setOpenDeleteDialog(false);
      
      // Keep loading overlay visible and navigate after short delay
      setTimeout(() => {
        navigate('/coaching/programs');
        // Don't set deleting to false here - let the navigation handle cleanup
      }, 1500);
      
    } catch (error: any) {
      console.error('Error deleting coaching program:', error);
      
      // Only set deleting to false on error
      setDeleting(false);
      
      // Check if error contains conflicting enrollments information
      if (error.response?.data?.activeEnrollments) {
        setConflictingEnrollments(error.response.data.activeEnrollments);
      } else {
        setConflictingEnrollments([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete coaching program';
      
      setError(errorMessage);
    }
  };

  const getDayName = (dayNumber: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber];
  };

  const handleDayOfWeekToggle = (dayNumber: number) => {
    const currentDays = formData.recurringPattern.daysOfWeek || [];
    let updatedDays;
    
    if (currentDays.includes(dayNumber)) {
      // Prevent removing the last day - there must be at least one day selected
      if (currentDays.length <= 1) {
        return; // Don't allow removing the last day
      }
      updatedDays = currentDays.filter(day => day !== dayNumber);
    } else {
      updatedDays = [...currentDays, dayNumber].sort();
    }
    
    setFormData(prev => ({
      ...prev,
      recurringPattern: {
        ...prev.recurringPattern,
        daysOfWeek: updatedDays
      }
    }));
  };

  const formatDurationDisplay = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else {
        return `${hours}h ${remainingMinutes}m`;
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading program details...</Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.COACH, UserRole.EMPLOYEE]}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Edit Coaching Program
          </Typography>
        </Box>

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {conflictingEnrollments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Active enrollments that would be affected:
                </Typography>
                {conflictingEnrollments.map((enrollment: any, index: number) => (
                  <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                    • {enrollment.memberName || enrollment.guestName} ({enrollment.memberEmail || enrollment.guestEmail})
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Program Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Program Type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Tennis, Swimming, Fitness"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe the program details, objectives, and target audience"
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <FormControl fullWidth required={!formData.isGuestCoach}>
                    <InputLabel>Coach</InputLabel>
                    <Select
                      value={formData.coachId || ''}
                      onChange={(e) => setFormData({ ...formData, coachId: e.target.value as number })}
                      label="Coach"
                      disabled={formData.isGuestCoach}
                    >
                      {coaches.map((coach) => (
                        <MenuItem key={coach.id} value={coach.id}>
                          {coach.firstName} {coach.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? null : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.guestCoachName}
                      onChange={(e) => setFormData({ ...formData, guestCoachName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  multiple
                  options={amenities}
                  getOptionLabel={(option) => `${option.name} (${option.type})`}
                  getOptionKey={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={amenities.filter(a => formData.venues.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, venues: newValue.map(a => a.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Venues"
                      required
                      placeholder="Select one or more venues/courts"
                      helperText="Select amenities to reserve for this program"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...chipProps}
                          size="small"
                        />
                      );
                    })
                  }
                />
              </Grid>

              {/* Slot Alignment Warning */}
              {slotValidationWarning && (
                <Grid size={{ xs: 12 }}>
                  <Alert 
                    severity={slotValidationWarning.includes('❌') ? 'error' : 'warning'}
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    <strong>Venue Slot Alignment:</strong>
                    {loadingVenueSchedules ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Checking venue schedules...</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        {slotValidationWarning}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}

              {/* Level and Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Maximum number of participants"
                />
              </Grid>

              {/* Dates */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleDateChange('startDate')}
                  disabled={Boolean(program && (program.status === 'ACTIVE' || new Date(program.startDate) <= new Date()))}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      helperText: program && (program.status === 'ACTIVE' || new Date(program.startDate) <= new Date())
                        ? "Cannot change start date of program that has already started"
                        : "Program start date"
                    } 
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleDateChange('endDate')}
                  minDate={formData.startDate}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      helperText: "Program end date (must be in the future)"
                    } 
                  }}
                />
              </Grid>

              {/* Session Time and Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Session Time"
                  value={formData.sessionTime}
                  onChange={(time) => {
                    if (time) {
                      setFormData({ ...formData, sessionTime: time });
                    }
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Session Duration (minutes)"
                  type="number"
                  value={formData.sessionDurationMinutes}
                  onChange={(e) => setFormData({ ...formData, sessionDurationMinutes: parseInt(e.target.value) || 60 })}
                  fullWidth
                  required
                  inputProps={{ min: 15, max: 480, step: 15 }}
                  helperText={`Duration: ${formatDurationDisplay(formData.sessionDurationMinutes)}`}
                />
              </Grid>

              {/* Enrollment Settings */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Enrollment Deadline (Optional)"
                  value={formData.enrollmentDeadline}
                  onChange={handleDateChange('enrollmentDeadline')}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      helperText: "Deadline for program enrollment"
                    } 
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowDropIns}
                      onChange={(e) => setFormData({ ...formData, allowDropIns: e.target.checked })}
                    />
                  }
                  label="Allow Drop-in Sessions"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>

            {/* Collapsible Recurring Configuration */}
            <Accordion expanded={recurringConfigExpanded} onChange={() => setRecurringConfigExpanded(!recurringConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Recurring Pattern Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Frequency */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={formData.recurringPattern.frequency}
                        onChange={(e) => setFormData({
                          ...formData,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            frequency: e.target.value as 'daily' | 'weekly' | 'monthly'
                          }
                        })}
                        label="Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Interval */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Repeat Interval"
                      type="number"
                      value={formData.recurringPattern.interval}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern,
                          interval: parseInt(e.target.value) || 1
                        }
                      })}
                      fullWidth
                      required
                      inputProps={{ min: 1, max: 52 }}
                      helperText={
                        formData.recurringPattern.frequency === 'daily' ? 'Every X days' :
                        formData.recurringPattern.frequency === 'weekly' ? 'Every X weeks' :
                        'Every X months'
                      }
                    />
                  </Grid>

                  {/* Days of Week (for weekly) */}
                  {formData.recurringPattern.frequency === 'weekly' && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Days of Week <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[0, 1, 2, 3, 4, 5, 6].map((dayNumber) => (
                          <Chip
                            key={dayNumber}
                            label={getDayName(dayNumber)}
                            onClick={() => handleDayOfWeekToggle(dayNumber)}
                            color={formData.recurringPattern.daysOfWeek.includes(dayNumber) ? 'primary' : 'default'}
                            variant={formData.recurringPattern.daysOfWeek.includes(dayNumber) ? 'filled' : 'outlined'}
                            clickable
                          />
                        ))}
                      </Box>
                      <FormHelperText>
                        Click to toggle days. At least one day must be selected.
                      </FormHelperText>
                    </Grid>
                  )}

                  {/* Day of Month (for monthly) */}
                  {formData.recurringPattern.frequency === 'monthly' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Day of Month"
                        type="number"
                        value={formData.recurringPattern.dayOfMonth || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            dayOfMonth: parseInt(e.target.value) || undefined
                          }
                        })}
                        fullWidth
                        required
                        inputProps={{ min: 1, max: 31 }}
                        helperText="Day of the month (1-31)"
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Collapsible Pricing Configuration */}
            <Accordion expanded={pricingConfigExpanded} onChange={() => setPricingConfigExpanded(!pricingConfigExpanded)} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Pricing Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Total Cost */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Total Program Cost"
                      type="number"
                      value={formData.totalCost}
                      onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })}
                      fullWidth
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Full program enrollment cost"
                    />
                  </Grid>

                  {/* Drop-in Cost */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Drop-in Session Cost"
                      type="number"
                      value={formData.dropInCost}
                      onChange={(e) => setFormData({ ...formData, dropInCost: e.target.value })}
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Per-session cost for drop-ins"
                      disabled={!formData.allowDropIns}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 3 }}
              placeholder="Internal notes about this program"
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
              {/* Delete button on the left */}
              <Button
                onClick={handleDeleteProgram}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={saving || deleting}
              >
                Delete Program
              </Button>
              
              {/* Cancel and Save buttons on the right */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={() => navigate(-1)}
                  disabled={saving || deleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving || deleting}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Reservation Results Dialog */}
        <ReservationResultsDialog
          open={reservationDialogOpen}
          onClose={() => {
            setReservationDialogOpen(false);
            setReservationDialogData(null);
            navigate(-1);
          }}
          entityType={reservationDialogData?.entityType || 'program'}
          entityName={reservationDialogData?.entityName || ''}
          operationType={reservationDialogData?.operationType || 'update'}
          reservationResult={reservationDialogData?.reservationResult || null}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => !deleting && setOpenDeleteDialog(false)}>
          <DialogTitle>Delete Coaching Program</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{program?.name}"? This action cannot be undone.
              {program && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    This will permanently remove the program and all its instances.
                  </Typography>
                </Box>
              )}
            </DialogContentText>
            
            {/* Error Alert in Delete Dialog */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
                {conflictingEnrollments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active enrollments that must be handled first:
                    </Typography>
                    {conflictingEnrollments.map((enrollment: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                        • {enrollment.memberName || enrollment.guestName} ({enrollment.memberEmail || enrollment.guestEmail})
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setOpenDeleteDialog(false);
                setError(null);
                setConflictingEnrollments([]);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteProgram} 
              color="error" 
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Program'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Overlay during Delete Operation */}
        <Backdrop
          sx={{ 
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.modal + 1,
            flexDirection: 'column',
            gap: 2
          }}
          open={deleting}
        >
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">
            Deleting coaching program...
          </Typography>
          <Typography variant="body2">
            Please wait while we remove the program and its instances
          </Typography>
        </Backdrop>
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default EditCoachingProgramPage;